import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { HiMiniSlash } from "react-icons/hi2";
import { BsGripVertical } from "react-icons/bs"; // NEW: drag handle icon
import * as XLSX from "xlsx"; // 👉 ADDED

/** ---------- Types ---------- */
type Attr = {
  id: number;
  quotationId: number;
  userId: number;
  qty: number;
  productId: number;
  companyId: number;
  price: number | null | undefined; // rate per unit
  discount: number;                 // %
  discountValue: number;
  discountAmount: number;
  gstAmount: number;
  totalPrice: number;
  createdDttm: number;
  gst_percentage: number;           // %
  companyProductCode?: string | null;
  gstValue?: number;
  quotationQty?: number;            // order qty
  productName: string;
  subCategoryName?: string | null;
  categoryName?: string | null;
  companyName: string;
  unitName: string;
  description?: string;
  productUnitCode?: string | null;
  departmentId?: number;
  categoryId?: number;
  srno?: number;
};

type InputRow = { id: number; name: string; attribute: Attr; quotationQty?: number; };
type HeaderCompany = { id: number; name: string; count: number; coverage: number };
type Props = {
  filteredprintexl: InputRow[];
  coverageThreshold?: number;  // default 0.30
  greenRows?: boolean;         // default true
  selecteduitable:any;
  isDiscountShow:any;
  isGstShow:any;
};

const FONT_SIZE_VIEW = "15px";
const PADDING_TOP_TABLE = "3px";
const PADDING_BOTTOM_TABLE = "3px";

const SlashSep = () => (
  <span className="slash-wrap" aria-hidden="true">
    {/* SVG for screen */}
    <HiMiniSlash className="slash-svg" style={{ width: "1.1em", height: "1.1em", color:"#000", WebkitPrintColorAdjust: "exact" }} />
    {/* Text for print */}
    <span className="slash-fallback"> / </span>
  </span>
);

/** ---------- Utils ---------- */
const norm = (s: any) => (s ?? "").toString().trim();
const toNum = (v: any, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);
const trimZeros = (n: number) => n.toFixed(4).replace(/\.?0+$/,"").replace(/(\.\d*[1-9])0+$/,"$1");
const fmt = (v: any) => trimZeros(toNum(v, 0));
const round0 = (n: number) => Math.round(n);
const hasPrice = (a?: Attr) => a != null && a.price != null && Number.isFinite(Number(a.price));
const packLabel = (a: Attr) => `${fmt(a.qty)} ${norm(a.unitName)}`;
const groupKey  = (a: Attr) =>
  `${norm(a.productName).toLowerCase()}|${fmt(a.qty)}|${norm(a.unitName).toLowerCase()}`;

const computeReqQtyAll = (items: Attr[]) => {
  const nz: number[] = [];
  for (let i = 0; i < items.length; i++) {
    const q = toNum(items[i].quotationQty, 0);
    if (q > 0) nz.push(q);
  }
  if (nz.length === 0) return 1;
  if (nz.every(q => q === nz[0])) return nz[0];
  if (nz.length === 1) return nz[0];
  return Math.max.apply(null, nz);
};

const uniformValue = (vals: number[]) => {
  const present = vals.filter(n => Number.isFinite(n));
  if (present.length === 0) return null;
  return present.every(n => n === present[0]) ? present[0] : null;
};

/** Join distinct numeric values as "a/b/c" (formatted), or "" if none are present */
const joinDistinctFmt = (vals: number[]) => {
  const present = vals.filter(n => Number.isFinite(n));
  if (present.length === 0) return "";
  const uniq: number[] = [];
  for (let i = 0; i < present.length; i++) {
    const v = present[i];
    if (uniq.indexOf(v) === -1) uniq.push(v);
  }
  return uniq.map(fmt).join("/");
};

/** NEW: Join distinct numeric values as "a%/b%/c%" */
const joinDistinctPct = (vals: number[]) => {
  const present = vals.filter(n => Number.isFinite(n));
  if (present.length === 0) return "";
  const uniq: number[] = [];
  for (let i = 0; i < present.length; i++) {
    const v = present[i];
    if (uniq.indexOf(v) === -1) uniq.push(v);
  }
  return uniq.map(v => `${fmt(v)}%`).join("/");
};

/** ---------- Component ---------- */
const OrderViewThreeTable: React.FC<Props> = ({
  filteredprintexl,
  coverageThreshold = 0.30,
  greenRows = true,
  selecteduitable,
  isDiscountShow,
  isGstShow
}) => {
  /** Group by product + pack */
  type Group = { key: string; productName: string; pack: string; items: Attr[] };
  const groups = useMemo<Group[]>(() => {
    const map: Record<string, Group> = {};
    const order: string[] = [];
    for (let i = 0; i < (filteredprintexl || []).length; i++) {
      const a = filteredprintexl[i].attribute; if (!a) continue;
      const k = groupKey(a);
      if (!map[k]) {
        map[k] = { key:k, productName: norm(a.productName) || norm(filteredprintexl[i].name), pack: packLabel(a), items: [] };
        order.push(k);
      }
      map[k].items.push(a);
    }
    return order.map(k => map[k]);
  }, [filteredprintexl]);

  /** ---- Drag & Drop (order entire product blocks without touching business logic) ---- */
  const defaultOrder = useMemo(() => groups.map(g => g.key), [groups]);
  const [groupOrder, setGroupOrder] = useState<string[]>(defaultOrder);
  useEffect(() => { setGroupOrder(defaultOrder); }, [defaultOrder]);

  const groupMap = useMemo(() => {
    const m: Record<string, Group> = {};
    for (const g of groups) m[g.key] = g;
    return m;
  }, [groups]);

  const orderedGroups = useMemo<Group[]>(
    () => (groupOrder.length ? groupOrder : defaultOrder).map(k => groupMap[k]).filter(Boolean) as Group[],
    [groupOrder, defaultOrder, groupMap]
  );

  const dragKeyRef = useRef<string | null>(null);
  const [overKey, setOverKey] = useState<string | null>(null);

  const reorder = useCallback((list: string[], src: string, dst: string) => {
    const next = list.slice();
    const from = next.indexOf(src);
    const to = next.indexOf(dst);
    if (from === -1 || to === -1) return list;
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
  }, []);

  // UPDATED: accept HTMLElement so it works for both <tr> and <button> drag starts
  const handleDragStart = useCallback((key: string) => (e: React.DragEvent<HTMLElement>) => {
    dragKeyRef.current = key;
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", key); } catch {}
    (e.currentTarget as HTMLElement).style.opacity = "0.6";
  }, []);

  // UPDATED: same reason (HTMLElement)
  const handleDragEnd = useCallback((e: React.DragEvent<HTMLElement>) => {
    (e.currentTarget as HTMLElement).style.opacity = "";
    dragKeyRef.current = null;
    setOverKey(null);
  }, []);

  const handleDragOver = useCallback((key: string) => (e: React.DragEvent<HTMLTableRowElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overKey !== key) setOverKey(key);
  }, [overKey]);

  const handleDrop = useCallback((key: string) => (e: React.DragEvent<HTMLTableRowElement>) => {
    e.preventDefault();
    const src = dragKeyRef.current;
    const dst = key;
    if (src && dst && src !== dst) setGroupOrder(prev => reorder(prev, src, dst));
    setOverKey(null);
    dragKeyRef.current = null;
  }, [reorder]);

  const draggableRowProps = (key: string) => ({
    draggable: true,
    onDragStart: handleDragStart(key),
    onDragEnd: handleDragEnd,
    onDragOver: handleDragOver(key),
    onDrop: handleDrop(key),
    style: overKey === key ? { background: "rgba(255,243,205,0.9)" } : undefined, // subtle highlight when targeting
  });

  /** Companies + presence */
  type Company = { id: number; name: string };
  const allCompanies: Company[] = useMemo(() => {
    const seen: Record<number,1> = {};
    const list: Company[] = [];
    for (const g of groups) {
      for (const a of g.items) {
        if (!seen[a.companyId]) { seen[a.companyId] = 1; list.push({ id:a.companyId, name:norm(a.companyName) }); }
      }
    }
    return list.sort((a,b)=>a.name.localeCompare(b.name));
  }, [groups]);

  type PresenceRow = { key: string; present: Record<number, boolean> };
  const presenceRows: PresenceRow[] = useMemo(() => {
    return groups.map(g => {
      const present: Record<number, boolean> = {};
      for (const a of g.items) present[a.companyId] = hasPrice(a);
      return { key:g.key, present };
    });
  }, [groups]);

  const indivCount: Record<number, number> = useMemo(() => {
    const out: Record<number, number> = {};
    for (const r of presenceRows) {
      for (const idStr of Object.keys(r.present)) {
        const id = Number(idStr);
        if (r.present[id]) out[id] = (out[id] || 0) + 1;
      }
    }
    return out;
  }, [presenceRows]);

  const totalGroups = groups.length || 1;

  /** Header companies via ≥30% complete rows (3→2→top-2) */
  const headerCompanies = useMemo<HeaderCompany[]>(() => {
    const ids = allCompanies.map(c=>c.id);
    const coverageFor = (S:number[]) => {
      let count = 0;
      for (const r of presenceRows) {
        let ok = true; for (const id of S) { if (!r.present[id]) { ok=false; break; } }
        if (ok) count++;
      }
      return count / totalGroups;
    };
    let bestSet:number[] = []; let bestCoverage = -1;
    if (ids.length >= 3) {
      for (let i=0;i<ids.length-2;i++)
        for (let j=i+1;j<ids.length-1;j++)
          for (let k=j+1;k<ids.length;k++){
            const S=[ids[i],ids[j],ids[k]]; const cov=coverageFor(S);
            if (cov>=coverageThreshold && cov>bestCoverage){ bestCoverage=cov; bestSet=S; }
          }
    }
    if (bestSet.length===0 && ids.length>=2) {
      for (let i=0;i<ids.length-1;i++)
        for (let j=i+1;j<ids.length;j++){
          const S=[ids[i],ids[j]]; const cov=coverageFor(S);
          if (cov>=coverageThreshold && cov>bestCoverage){ bestCoverage=cov; bestSet=S; }
        }
    }
    if (bestSet.length===0) {
      const sorted = allCompanies.slice().sort(
        (a,b)=>(indivCount[b.id]||0)-(indivCount[a.id]||0)||a.name.localeCompare(b.name)
      );
      bestSet = sorted.slice(0, Math.min(2, sorted.length)).map(c=>c.id);
      bestCoverage = coverageFor(bestSet);
    }
    const nameById: Record<number,string> = {};
    allCompanies.forEach(c=>nameById[c.id]=c.name);
    const list = bestSet.map(cid=>({ id:cid, name:nameById[cid]||`Company ${cid}`, count:indivCount[cid]||0, coverage:bestCoverage }));
    return list.sort((a,b)=>a.name.localeCompare(b.name));
  }, [allCompanies, presenceRows, indivCount, totalGroups, coverageThreshold]);

  const headerIds = new Set(headerCompanies.map(c=>c.id));
  const SUBCOLS = Math.max(1, headerCompanies.length);
  const borderColor =
    selecteduitable === "SOHAM FIRM" ? "rgb(166, 166, 192)" : "rgb(175 193 123 / 49%)";
  const bgColor =
    selecteduitable === "SOHAM FIRM" ? "rgb(166, 166, 192)" : "rgb(175 193 123 / 78%)";

  const styles: Record<string, React.CSSProperties> = {
    left:{textAlign:"left" as const}, center:{textAlign:"center" as const},
    tableWrap: { marginBottom: 12 },
    table: {
      width: "100%",
      borderCollapse: "collapse" as const,
      fontFamily: "Arial, sans-serif",
      border: `2px solid ${borderColor}`,
    },
    th: {
      padding: "6px",
      border: `1px solid ${borderColor}`,
      fontWeight: "bold",
      fontSize: FONT_SIZE_VIEW,
      textAlign: "center" as const,
      wordWrap: "break-word" as const,
      paddingTop: PADDING_TOP_TABLE,
      paddingBottom: PADDING_BOTTOM_TABLE,
      backgroundColor: bgColor,  color: "#2c2c2c"
    },
    td: {
      padding: "6px",
      border: `1px solid ${borderColor}`,
      fontSize: FONT_SIZE_VIEW,
      textAlign: "center" as const,
      paddingTop: PADDING_TOP_TABLE,
      paddingBottom: PADDING_BOTTOM_TABLE,
      cursor: "default",
    },
    tableCellEditable: {
      padding: "6px",
      border: `1px solid ${borderColor}`,
      fontSize: FONT_SIZE_VIEW,
      textAlign: "center" as const,
      paddingTop: PADDING_TOP_TABLE,
      paddingBottom: PADDING_BOTTOM_TABLE,
      cursor: "text",
      background: "rgba(255,255,0,0.06)",
    },
    input: {
      width: "100%",
      boxSizing: "border-box" as const,
      fontSize: FONT_SIZE_VIEW,
      padding: "2px 4px",
      textAlign: "right" as const,
    },
    tableCell3: {
      whiteSpace: "normal" as const,
      overflow: "hidden",
      textOverflow: "ellipsis",
      textAlign: "left" as const,
    },
    sectionHeaderCell: {
      padding: "6px",
      border: `1px solid ${borderColor}`,
      fontSize: "12px",
      fontWeight: 700 as const,
      textAlign: "left" as const,
      backgroundColor: "rgba(0,0,0,0.03)",
    },
    totalCell: {
      padding: "6px",
      textAlign: "center" as const,
      fontWeight: "bold",
      fontSize: FONT_SIZE_VIEW,
      borderTop: `2px solid ${borderColor}`,
      paddingTop: PADDING_TOP_TABLE,
      paddingBottom: PADDING_BOTTOM_TABLE,
    },
    totalAmount: {
      padding: "6px",
      fontWeight: "bold",
      textAlign: "center" as const,
      fontSize: FONT_SIZE_VIEW,
      borderLeft: `2px solid ${borderColor}`,
      borderTop: `2px solid ${borderColor}`,
      paddingTop: PADDING_TOP_TABLE,
      paddingBottom: PADDING_BOTTOM_TABLE,
    },
    srCellInner: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 },
    iconBtn: {
      border: "none",
      background: "transparent",
      cursor: "pointer",
      padding: 0,
      fontSize: 12,
      lineHeight: 1,
    },
    smallIcon: { fontSize: 12 },
  };
  /** ---------- Styles ---------- */
  const B="#333";
  const rowBg = greenRows ? "rgb(232 244 219)" : "#fff";

  /** Fixed column widths — now conditional on isDiscountShow / isGstShow */
  const renderColGroup = () => {
    const cols: React.ReactNode[] = [];
    const push = (w: number | string) =>
      cols.push(<col key={`c-${cols.length}`} style={{ width: typeof w === "number" ? `${w}px` : w ,fontSize:"25px !important;"}} />);

    // Static columns
    push(50);   // Sr. No.
    push(300);  // Name
    push(100);  // Make
    push(70);  // Pack

    // Price (per company)
    for (let i = 0; i < SUBCOLS; i++) push(70);

    // Discount block (optional)
    if (isDiscountShow) {
      push(70); // Rate Dis. % (single)
      for (let i = 0; i < SUBCOLS; i++) push(70); // Dis. Amt (per company)
    }

    // GST (optional)
    if (isGstShow) push(70);

    // Qty (always shown)
    push(70);

    // Final Amount (per company)
    for (let i = 0; i < SUBCOLS; i++) push(70);

    return <colgroup>{cols}</colgroup>;
  };

  /** Helper: render a company group (Price / DisAmt / Final) with colSpan collapse */
  const renderGroupCells = (values: Array<number | null | undefined>, keyPrefix: string) => {
    let present = 0, idx = -1;
    for (let i=0;i<values.length;i++) if (values[i] != null && Number.isFinite(Number(values[i]))) { present++; idx = i; }
    if (present === 0) return <td key={`${keyPrefix}-blank`} style={styles.td} colSpan={SUBCOLS}></td>;
    if (present === 1) {
      return (
        <td key={`${keyPrefix}-one`} style={{...styles.td, ...styles.center}} colSpan={SUBCOLS}>
          {fmt(values[idx] as number)}
        </td>
      );
    }
    const tds: React.ReactNode[] = [];
    for (let i=0;i<SUBCOLS;i++) {
      tds.push(
        <td key={`${keyPrefix}-${i}`} style={{...styles.td, ...styles.center}}>
          {values[i] != null && Number.isFinite(Number(values[i])) ? fmt(values[i] as number) : ""}
        </td>
      );
    }
    return tds;
  };

  let sr = 0; const nextSr = () => (++sr);

  // 👉 ADDED: ref to wrap & export function
  const tablesWrapRef = useRef<HTMLDivElement | null>(null);

 const exportToExcel = () => {
  const container = tablesWrapRef.current;
  if (!container) return;

  const wb = XLSX.utils.book_new();
  const table = container.querySelector("table");
  if (!table) return;

  // clone & strip .no-print content so it doesn't get exported as values
  const clone = table.cloneNode(true) as HTMLTableElement;
  clone.querySelectorAll(".no-print").forEach((el) => el.remove());
  clone.querySelectorAll("th.no-print, td.no-print").forEach((cell) => {
    (cell as HTMLElement).innerHTML = "";
  });

  const ws = XLSX.utils.table_to_sheet(clone, { raw: true });

  // --- Optional nicety: column widths from <colgroup>
  const cols = table.querySelectorAll("colgroup col");
  if (cols.length) {
    const wcols: Array<{ wch: number }> = [];
    cols.forEach((c) => {
      const w = (c as HTMLElement).style?.width || "";
      if (w.endsWith("px")) {
        const px = parseFloat(w) || 0;
        wcols.push({ wch: Math.max(8, Math.round(px / 8)) });
      } else {
        wcols.push({ wch: 12 });
      }
    });
    (ws as any)["!cols"] = wcols;
  }

  // --- NEW: center-align Price, Dis. Amt and Final Amount columns in Excel
  // We know the column layout from renderColGroup():
  // [Sr, Name, Make, Pack] + Price(SUBCOLS) + [Rate Dis.%?] + Dis.Amt(SUBCOLS?) + [GST?] + Qty + Final(SUBCOLS)
  const priceStart = 4; // after Sr, Name, Make, Pack
  const priceCols: number[] = [];
  for (let i = 0; i < SUBCOLS; i++) priceCols.push(priceStart + i);

  let offset = priceStart + SUBCOLS;

  const disAmtCols: number[] = [];
  if (isDiscountShow) {
    const rateDisCol = offset; // single column we do NOT center as per request
    offset = rateDisCol + 1;
    for (let i = 0; i < SUBCOLS; i++) disAmtCols.push(offset + i);
    offset += SUBCOLS;
  }

  if (isGstShow) {
    const gstCol = offset; // not requested to center
    offset = gstCol + 1;
  }

  const qtyCol = offset; // not requested to center
  const finalStart = qtyCol + 1;
  const finalCols: number[] = [];
  for (let i = 0; i < SUBCOLS; i++) finalCols.push(finalStart + i);

  const centerCols = [...priceCols, ...disAmtCols, ...finalCols];

  if (ws["!ref"]) {
    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (const C of centerCols) {
      for (let R = range.s.r; R <= range.e.r; ++R) {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = (ws as any)[addr];
        if (!cell) continue;
        cell.s = cell.s || {};
        cell.s.alignment = { horizontal: "center", vertical: "center" };
      }
    }
  }

  XLSX.utils.book_append_sheet(wb, ws, "Table");
  const fname = `Quotation_${new Date().toISOString().slice(0, 10)}.xlsx`;
  XLSX.writeFile(wb, fname);
};


  return (
    <div style={{paddingRight:"10px"}} ref={tablesWrapRef}>
      {/* NEW: helper styles (grab cursor + hide handle in print) */}
      <style>{`
        .drag-handle{ cursor: grab; user-select: none; -webkit-user-drag: element; }
        .drag-handle:active{ cursor: grabbing; }
        @media print { .no-print { display: none !important; } }
      `}</style>

      {/* 👉 ADDED: Download Excel button (hidden on print) */}
      <div className="no-print" style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <button
          type="button"
          onClick={exportToExcel}
          style={{
            border: `1px solid ${borderColor}`,
            background: "#fff",
            padding: "6px 10px",
            borderRadius: 6,
            cursor: "pointer",
            fontSize: 12,
          }}
        >
          ⬇️ Download Excel
        </button>
      </div>

      <table style={styles.table}>
        {renderColGroup()}
        <thead>
          <tr>
            <th style={styles.th} rowSpan={2}>Sr. No.</th>
            <th style={styles.th} rowSpan={2}>Name of Chemicals</th>
            <th style={styles.th} rowSpan={2}>Make</th>
            <th style={styles.th} rowSpan={2}>Pack</th>

            <th style={styles.th} colSpan={SUBCOLS}>Price</th>
            {isDiscountShow ? <>
              <th style={styles.th} rowSpan={2}>Rate Dis.%</th>
              <th style={styles.th} colSpan={SUBCOLS}>Dis. Amt</th>
            </> : null}
            {isGstShow ? <th style={styles.th} rowSpan={2}>GST</th> : null}
            <th style={styles.th} rowSpan={2}>Qty</th>
            <th style={styles.th} colSpan={SUBCOLS}>Final Amount</th>
          </tr>
          <tr>
            {headerCompanies.map(c => <th key={`h-price-${c.id}`} style={styles.th}>{c.name}</th>)}
            {isDiscountShow ? headerCompanies.map(c => <th key={`h-discamt-${c.id}`} style={styles.th}>{c.name}</th>) : null}
            {headerCompanies.map(c => <th key={`h-final-${c.id}`} style={styles.th}>{c.name}</th>)}
          </tr>
        </thead>

        <tbody>
          {orderedGroups.map((g) => {
            const byCompany: Record<number, Attr> = {};
            for (const a of g.items) byCompany[a.companyId] = a;

            const reqQtyAll = computeReqQtyAll(g.items);

            const headerPresent = headerCompanies
              .map(h => byCompany[h.id])
              .filter(a => !!a && hasPrice(a as Attr)) as Attr[];

            const others = g.items.filter(a => !headerIds.has(a.companyId));
            const hasHeaderData = headerPresent.length > 0;

            const rows: React.ReactNode[] = [];

            if (hasHeaderData) {
              const serial = nextSr();

              const names = headerCompanies
                .map(h => byCompany[h.id])
                .filter(a => !!a && hasPrice(a as Attr))
                .map(a => norm(a!.companyName))
                .filter((name, i, arr) => name && arr.indexOf(name) === i);

              const makeJoined = () => (
                <div style={{display:"flex",justifyContent:"center",alignItems:"center"}}>
                  {names.map((n, i) => (
                    <React.Fragment key={n + i}>
                      {i > 0 && <SlashSep />}
                      {n}
                    </React.Fragment>
                  ))}
                </div>
              );

              // Arrays aligned to header order
              const priceVals: Array<number | null> = [];
              const discAmtVals: Array<number | null> = [];
              const finalVals: Array<number | null> = [];

              const disList: number[] = [];
              const gstList: number[] = [];
              const qtyList: number[] = [];

              for (let i=0;i<headerCompanies.length;i++) {
                const a = byCompany[headerCompanies[i].id];
                if (hasPrice(a)) {
                  const price = toNum(a!.price, 0);
                  const disPct = toNum(a!.discount, 0);
                  const discountAmtPerUnit = price * disPct / 100;
                  const priceAfterDiscount = price - discountAmtPerUnit;
                  const qty = toNum(a!.quotationQty, reqQtyAll);
                  const netPerUnit = priceAfterDiscount;
                  const gstPct = toNum(a!.gst_percentage, 0);
                  const totalBeforeGst = netPerUnit * qty;
                  const final = totalBeforeGst * (1 + gstPct/100);

                  priceVals.push(price);
                  discAmtVals.push(priceAfterDiscount);
                  finalVals.push(final);

                  disList.push(disPct);
                  gstList.push(gstPct);
                  qtyList.push(qty);
                } else {
                  priceVals.push(null); discAmtVals.push(null); finalVals.push(null);
                }
              }

              // (kept for minimal change) — not used now:
              const disUniform = uniformValue(disList);
              const gstUniform = uniformValue(gstList);
              const qtyDisplay = joinDistinctFmt(qtyList);

              // NEW: display values that join distinct entries like qty
              const disDisplay = joinDistinctPct(disList);
              const gstDisplay = joinDistinctPct(gstList);

              rows.push(
                <tr key={`${g.key}-main`} {...draggableRowProps(g.key)}>
                  {/* NEW: drag handle + serial */}
                  <td style={{...styles.td, ...styles.left,...{textAlign:"left"}}} >
                    <div style={styles.srCellInner}>
                      <button
                        type="button"
                        className="drag-handle no-print"
                        title="Drag to reorder"
                        draggable
                        onDragStart={handleDragStart(g.key)}
                        onDragEnd={handleDragEnd}
                        style={styles.iconBtn}
                      >
                        <BsGripVertical style={styles.smallIcon} />
                      </button>
                      <span>{serial}</span>
                    </div>
                  </td>

                  <td style={{...styles.td, ...styles.left}}>{g.productName}</td>
                  <td style={{...styles.td, ...styles.center}}>{makeJoined()}</td>
                  <td style={{...styles.td, ...styles.center}}>{g.pack}</td>

                  {/* Price (per company) */}
                  {renderGroupCells(priceVals, `${g.key}-price`)}

                  {/* Rate Dis.% (single cell, now shows distinct values if different) */}
                  {isDiscountShow ? (
                    <td style={{...styles.td, ...styles.center}}>
                      {disDisplay}
                    </td>
                  ) : null}

                  {/* Discount Amount (per company) */}
                  {isDiscountShow ? renderGroupCells(discAmtVals, `${g.key}-discamt`) : null}

                  {/* GST (single cell, now shows distinct values if different) */}
                  {isGstShow ? (
                    <td style={{...styles.td, ...styles.center}}>
                      {gstDisplay}
                    </td>
                  ) : null}

                  {/* Qty (single cell, join distinct) */}
                  <td style={{ ...styles.td, ...styles.center }}>{qtyDisplay}</td>

                  {/* Final Amount (per company) */}
                  {renderGroupCells(finalVals, `${g.key}-final`)}
                </tr>
              );

              // OTHER companies (own values; no rowSpan)
              for (let i=0;i<others.length;i++) {
                const a = others[i];

                const price = hasPrice(a) ? toNum(a.price,0) : NaN;
                const disPct = toNum(a.discount, 0);
                const discountAmtPerUnit = Number.isFinite(price) ? price * disPct / 100 : NaN;
                const priceAfterDiscount = Number.isFinite(price) ? price - discountAmtPerUnit : NaN;
                const qty = toNum(a.quotationQty, reqQtyAll);
                const netPerUnit = priceAfterDiscount;
                const gstPct = toNum(a.gst_percentage, 0);
                const totalBeforeGst = Number.isFinite(netPerUnit) ? netPerUnit * qty : NaN;
                const final = Number.isFinite(totalBeforeGst) ? totalBeforeGst * (1 + gstPct/100) : NaN;

                const serial2 = nextSr();
                rows.push(
                  <tr key={`${g.key}-other-${i}`}>
                    <td style={{...styles.td, ...{textAlign:"left"}}} >{serial2}</td>
                    <td style={{...styles.td, ...styles.left}}>{g.productName}</td>
                    <td style={{...styles.td, ...styles.center}}>{a.companyName}</td>
                    <td style={{...styles.td, ...styles.center}}>{g.pack}</td>

                    {/* Price */}
                    <td style={{...styles.td, ...styles.center}} colSpan={SUBCOLS}>
                      {hasPrice(a) ? fmt(price) : ""}
                    </td>

                    {isDiscountShow ? <>
                      <td style={{...styles.td, ...styles.center}}>
                        {Number.isFinite(disPct) ? `${fmt(disPct)}%` : ""}
                      </td>
                      <td style={{...styles.td, ...styles.center}} colSpan={SUBCOLS}>
                        {Number.isFinite(priceAfterDiscount) ? fmt(priceAfterDiscount) : ""}
                      </td>
                    </> : null}

                    {isGstShow ? (
                      <td style={{...styles.td, ...styles.center}}>
                        {Number.isFinite(gstPct) ? `${fmt(gstPct)}%` : ""}
                      </td>
                    ) : null}

                    {/* Qty */}
                    <td style={{...styles.td, ...styles.center}}>
                      {Number.isFinite(qty) ? fmt(qty) : ""}
                    </td>

                    {/* Final Amount */}
                    <td style={{...styles.td, ...styles.center}} colSpan={SUBCOLS}>
                      {Number.isFinite(final) ? fmt(final) : ""}
                    </td>
                  </tr>
                );
              }
            } else {
              // Compact layout (no header-company price)
              const list = g.items;
              const n = list.length;

              for (let i=0;i<list.length;i++) {
                const a = list[i];

                const price = hasPrice(a) ? toNum(a.price,0) : NaN;
                const disPct = toNum(a.discount, 0);
                const discountAmtPerUnit = Number.isFinite(price) ? price * disPct / 100 : NaN;
                const priceAfterDiscount = Number.isFinite(price) ? price - discountAmtPerUnit : NaN;
                const qty = toNum(a.quotationQty, computeReqQtyAll(list));
                const netPerUnit = priceAfterDiscount;
                const gstPct = toNum(a.gst_percentage, 0);
                const totalBeforeGst = Number.isFinite(netPerUnit) ? netPerUnit * qty : NaN;
                const final = Number.isFinite(totalBeforeGst) ? totalBeforeGst * (1 + gstPct/100) : NaN;

                const serial3 = nextSr();
                const firstRowProps = i === 0 ? draggableRowProps(g.key) : {};

                rows.push(
                  <tr key={`${g.key}-compact-${i}`} {...firstRowProps}>
                    {/* NEW: handle only on the first row of the group */}
                    <td style={{...styles.td, ...styles.left,...{textAlign:"left"}}}>
                      {i === 0 ? (
                        <div style={styles.srCellInner}>
                          <button
                            type="button"
                            className="drag-handle no-print"
                            title="Drag to reorder"
                            draggable

                            onDragStart={handleDragStart(g.key)}
                            onDragEnd={handleDragEnd}
                            style={styles.iconBtn}
                          >
                            <BsGripVertical style={styles.smallIcon} className="no-print " />
                          </button>
                          <span>{serial3}</span>
                        </div>
                      ) : (
                        <span>{serial3}</span>
                      )}
                    </td>

                    {i === 0 && (
                      <td style={{...styles.td, ...styles.left}} rowSpan={n}>{g.productName}</td>
                    )}
                    <td style={{...styles.td, ...styles.center}}>{a.companyName}</td>
                    {i === 0 && (
                      <td style={{...styles.td, ...styles.center}} rowSpan={n}>{g.pack}</td>
                    )}

                    <td style={{...styles.td, ...styles.center}} colSpan={SUBCOLS}>
                      {hasPrice(a) ? fmt(price) : ""}
                    </td>

                    {isDiscountShow ? <>
                      <td style={{...styles.td, ...styles.center}}>
                        {Number.isFinite(disPct) ? `${fmt(disPct)}%` : ""}
                      </td>
                      <td style={{...styles.td, ...styles.center}} colSpan={SUBCOLS}>
                        {Number.isFinite(priceAfterDiscount) ? fmt(priceAfterDiscount) : ""}
                      </td>
                    </> : null}

                    {isGstShow ? (
                      <td style={{...styles.td, ...styles.center}}>
                        {Number.isFinite(gstPct) ? `${fmt(gstPct)}%` : ""}
                      </td>
                    ) : null}

                    {/* Qty (per row) */}
                    <td style={{...styles.td, ...styles.center}}>
                      {Number.isFinite(qty) ? fmt(qty) : ""}
                    </td>

                    <td style={{...styles.td, ...styles.center}} colSpan={SUBCOLS}>
                      {Number.isFinite(final) ? fmt(final) : ""}
                    </td>
                  </tr>
                );
              }
            }

            return rows;
          })}
        </tbody>
      </table>
    </div>
  );
};

export default OrderViewThreeTable;
